import { AsgmtSubmissionStreamActivityUpdate } from 'common-utils/interfaces';
import { AssignmentStreamActivityRI, AssignmentSubmissionNotificationRI } from 'common-utils/rest-interfaces';
import { map, publishLast, refCount, mergeMap, filter } from 'rxjs/operators';
import { Observable, of, Subject } from 'rxjs';
import { AjaxResponse } from '../interfaces/AjaxResponse';
import { AssignmentSubmissionNotificationServiceConfig } from '../interfaces/assignment-submission-notification-service-config';
import { AssignmentSubmissionNotificationServiceContext } from '../interfaces/assignment-submission-notification-service-context';
import { AsgmtSubmissionNotificationItem, AsgmtSubmissionNotificationItemContext } from '../models/asgmt-submission-notification-item';
import { pdsHttpUtils } from '../pds-http-utils';
import { GetStreamFeedService } from '../services/get-stream-feed-service';
import { NotificationService } from './notification-service';
import { SectionRI } from 'common-utils/rest-interfaces';

// this class is the base interface of services used by the aggregated-notification-service for the notification indicator
export class AsgmtSubmissionNotificationService extends NotificationService {
  notificationStream: Observable<AsgmtSubmissionNotificationItem> = new Subject<AsgmtSubmissionNotificationItem>();

  type: string = AsgmtSubmissionNotificationItem.NOTIFICATION_SUBTYPE;
  config: AssignmentSubmissionNotificationServiceConfig;
  context: AssignmentSubmissionNotificationServiceContext;

  private getStreamFeed: GetStreamFeedService;
  private feedInitialized: Observable<any>;
  private userNameMap: { [userUid: string]: string } = {};

  constructor(setup: any, getStreamClient: any) {
    super(setup);

    this.config = setup;
    this.context = setup.context;

    let payload = {
      feedUid: this.context.currentUserUid,
      feedGroup: this.config.feedGroup
    };

    let path = (this.config.communicationPath == null) ? '/communication/v1/token' : this.config.communicationPath;
    let url = this.config.domain != null ? this.config.domain + path : path;
    this.feedInitialized = pdsHttpUtils.post(url, payload, {}, pdsHttpUtils.addUCAuthHeader({})).pipe(
      map((response: AjaxResponse) => {
        let data: { feedGroup: string, token: string, uid: string } = JSON.parse(response.data);
        this.getStreamFeed = new GetStreamFeedService(getStreamClient, this.config.feedGroup, `${this.config.feedPrefix}${this.context.currentUserUid}`, data.token, true);
      }),
      publishLast(),
      refCount());

  }

  markAsRead(items: Array<AsgmtSubmissionNotificationItem>): Observable<any> {
    return this.getStreamFeed.markNotificationsRead(items.map((item) => {
      return item.getstreamGroupId;
    }));
  }

  markAsSeen(items: Array<AsgmtSubmissionNotificationItem>): Observable<any> {
    return of({});
  }

  initializeNotificationStream(): Observable<any> {
    let filterCallback = (update: AsgmtSubmissionStreamActivityUpdate): boolean => {
      return update.newActivity != null;
    };

    return this.feedInitialized.pipe( // these bangs are actually safe because of filter callback
      map(() => {
        this.notificationStream = this.getStreamFeed.realTimeUpdates().pipe(
          filter(filterCallback),
          map((update: AsgmtSubmissionStreamActivityUpdate) => {
            let section = this.context.sectionMap[update.newActivity!.sectionUid];
            let context: AsgmtSubmissionNotificationItemContext = {
              activity: update.newActivity!,
              userType: this.context.userType,
              currentUserUid: this.context.currentUserUid,
              getStreamGroupId: update.newActivity!.group!,
              sectionName: this.getDisplaySectionNane(section)
            };
            return new AsgmtSubmissionNotificationItem(false, false, context);
          }));
        return true;
      }));
  }

  getRecentNotifications(limit: number): Observable<Array<AsgmtSubmissionNotificationItem>> {
    let filterCallBack = (notification: AssignmentSubmissionNotificationRI) => {
      return (notification.activities != null && notification.activities.length > 0) ? !notification.is_read && Object.keys(this.context.sectionMap).indexOf(notification.activities[0].sectionUid) >= 0 : false;
    };
    return this.feedInitialized.pipe(
      mergeMap(() => {
        return this.getStreamFeed.getNotifications(limit);
      }),
      map((notifications: Array<AssignmentSubmissionNotificationRI>) => {
        return notifications.filter(filterCallBack).map((notificationRI: AssignmentSubmissionNotificationRI) => {
          let activity: AssignmentStreamActivityRI = notificationRI.activities[0];
          let section = this.context.sectionMap[activity.sectionUid];
          let context: AsgmtSubmissionNotificationItemContext = {
            activity: activity,
            userType: this.context.userType,
            currentUserUid: this.context.currentUserUid,
            getStreamGroupId: notificationRI.group!,
            sectionName: this.getDisplaySectionNane(section)
          };
          return new AsgmtSubmissionNotificationItem(notificationRI.is_seen, notificationRI.is_read, context);
        });
      }));
  }

  private getDisplaySectionNane(section: SectionRI | undefined): string {
    if (section == null) {
      return '';
    }
    if (section.secondaryDisplay == null) {
      return section.primaryDisplay == null ? '' : section.primaryDisplay;
    }
    return section.secondaryDisplay + ' ' + section.primaryDisplay;
  }
}
